/**
 * CHILD ENTRY FORM CONFIG REGISTRY
 *
 * Client-side singleton registry for child entry form configuration.
 * Loaded from YAML at app startup via configLoader.server.ts.
 * Provides in-memory lookups for entry type configs, selection options,
 * and SmartAutomation capture button categories.
 */

import type {
  ChildEntryFormConfig,
  EntryTypeConfigYaml,
  EntryTypeOptionYaml,
  ConfigurableEntryTypeYaml,
  CaptureProfileOptionYaml,
  ReceiptCategoryExpectedMapYaml,
  CameraConfigYaml,
} from './types';

export class ChildEntryFormRegistry {
  private entryConfigs: Map<string, EntryTypeConfigYaml> = new Map();
  private expenseEntryTypes: string[] = [];
  private defaultPaymentMethods: Record<string, string> = {};
  private purchaseCategoryOptions: string[] = [];
  private captureProfileOptions: CaptureProfileOptionYaml[] = [];
  private entryTypeOptions: EntryTypeOptionYaml[] = [];
  private configurableEntryTypes: ConfigurableEntryTypeYaml[] = [];
  private receiptCategoryExpectedMap: ReceiptCategoryExpectedMapYaml = {};
  private childEntryFormZIndex: number = 200;
  private cameraDefaults: CameraConfigYaml | null = null;
  private cameraConfigs: Record<string, CameraConfigYaml> = {};
  private initialized = false;

  loadConfig(config: ChildEntryFormConfig): void {
    this.entryConfigs.clear();

    if (config.entry_configs) {
      for (const [key, entry] of Object.entries(config.entry_configs)) {
        this.entryConfigs.set(key, entry);
      }
    }

    this.expenseEntryTypes = config.expense_entry_types || [];
    this.defaultPaymentMethods = config.default_payment_methods || {};
    this.purchaseCategoryOptions = config.purchase_category_options || [];
    this.captureProfileOptions = config.capture_profile_options || [];
    this.entryTypeOptions = config.entry_type_options || [];
    this.configurableEntryTypes = config.configurable_entry_types || [];
    this.receiptCategoryExpectedMap = config.receipt_category_expected_map || {};
    this.childEntryFormZIndex = config.child_entry_form_z_index ?? 200;
    this.cameraDefaults = config.camera_defaults || null;
    this.cameraConfigs = config.camera_configs || {};

    this.initialized = true;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  getEntryConfig(entryType: string): EntryTypeConfigYaml | null {
    return this.entryConfigs.get(entryType) || null;
  }

  getAllEntryConfigs(): Map<string, EntryTypeConfigYaml> {
    return this.entryConfigs;
  }

  getExpenseEntryTypes(): string[] {
    return this.expenseEntryTypes;
  }

  getDefaultPaymentMethods(): Record<string, string> {
    return this.defaultPaymentMethods;
  }

  getDefaultPaymentMethod(entryType: string): string | undefined {
    return this.defaultPaymentMethods[entryType];
  }

  getPurchaseCategoryOptions(): string[] {
    return this.purchaseCategoryOptions;
  }

  getCaptureProfileOptions(): CaptureProfileOptionYaml[] {
    return this.captureProfileOptions;
  }

  getEntryTypeOptions(): EntryTypeOptionYaml[] {
    return this.entryTypeOptions;
  }

  getConfigurableEntryTypes(): ConfigurableEntryTypeYaml[] {
    return this.configurableEntryTypes;
  }

  getReceiptCategoryExpectedMap(): ReceiptCategoryExpectedMapYaml {
    return this.receiptCategoryExpectedMap;
  }

  getReceiptCategoryExpected(entryType: string): string[] | undefined {
    return this.receiptCategoryExpectedMap[entryType];
  }

  getChildEntryFormZIndex(): number {
    return this.childEntryFormZIndex;
  }

  /**
   * Get the resolved camera config for an entry type.
   * Merges camera_defaults with per-type overrides from camera_configs.
   * Returns null if no camera config is defined (caller should use its own defaults).
   */
  getCameraConfig(entryType: string): CameraConfigYaml | null {
    const overrides = this.cameraConfigs[entryType];
    if (!this.cameraDefaults && !overrides) return null;
    return {
      ...(this.cameraDefaults || {}),
      ...(overrides || {}),
    };
  }

  getCameraDefaults(): CameraConfigYaml | null {
    return this.cameraDefaults;
  }
}

let registryInstance: ChildEntryFormRegistry | null = null;

export function getChildEntryFormRegistry(): ChildEntryFormRegistry {
  if (!registryInstance) {
    registryInstance = new ChildEntryFormRegistry();
  }
  return registryInstance;
}
